import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, ProviderLogo } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useStartOAuth } from '@/hooks/useIdentityCredentials';
import {
  authMethodLabel,
  deriveLogoSlug,
  isDeviceStart,
  openIdentityOAuthUrl,
  providerRowKey,
} from './identity-lib';
import AddApiKeyModal from './AddApiKeyModal';
import AddAwsRoleModal from './AddAwsRoleModal';
import ConnectOAuthTokenModal from './ConnectOAuthTokenModal';
import DeviceAuthModal from './DeviceAuthModal';
import type {
  CredentialAuthType,
  OAuthDeviceStart,
  ProviderSummary,
} from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  providers: ProviderSummary[];
  scope: Scope;
  canManage: boolean;
  /**
   * Composite row keys (`${id}::${authType}::${providerInstanceId}`) that
   * already have at least one credential in the rendered scope. A provider can
   * appear in more than one row (e.g. Datadog via oauth_manual *and* api_key),
   * so excluding by composite key — not bare provider id — keeps a row that's
   * still disconnected visible even when a sibling method is connected.
   */
  connectedKeys: Set<string>;
}

/**
 * Bottom section listing every provider row with no credential yet in the
 * active scope. Sorted alphabetically by display name. Hidden when the caller
 * can't manage anything in this scope (no point dangling a Connect button that
 * 403s).
 */
export default function AvailableProvidersList({
  providers,
  scope,
  canManage,
  connectedKeys,
}: Props) {
  const rows = providers
    // Skip half-populated entries (no id or no supportedAuthTypes) so the
    // page doesn't crash when the catalog response ships partial records.
    .filter((p) => !!p?.id && Array.isArray(p.supportedAuthTypes) && !!p.supportedAuthTypes[0])
    // Expand each provider into one row per auth method it advertises. The
    // platform catalog ships a provider that supports several methods as a
    // single entry (e.g. Atlassian = ['oauth2', 'api_key']); reading only
    // `[0]` would silently drop every method but the first. MCP-derived
    // summaries already carry a single-element array, so this is a no-op for
    // them.
    .flatMap((p) =>
      p.supportedAuthTypes.map((authType) => ({ provider: p, authType })),
    )
    // Drop rows that already have a credential in this scope, matched by the
    // same composite key the connected cards use.
    .filter(
      ({ provider, authType }) =>
        !connectedKeys.has(
          providerRowKey(provider.id, authType, provider.providerInstanceId),
        ),
    )
    // Org scope has no OAuth-start route, so hide interactive-OAuth rows
    // there — they'd dangle a non-functional Connect button. User and space
    // scopes have all three auth paths.
    .filter(({ authType }) => scope.kind !== 'org' || authType !== 'oauth2')
    // AWS IAM roles attach to a shared space or the org — never personal.
    .filter(({ authType }) => scope.kind !== 'user' || authType !== 'aws_role')
    .sort((a, b) => {
      const byName = (a.provider.displayName ?? a.provider.id ?? '').localeCompare(
        b.provider.displayName ?? b.provider.id ?? '',
      );
      if (byName !== 0) return byName;
      return a.authType.localeCompare(b.authType);
    });

  if (!canManage || rows.length === 0) return null;

  // A provider id appearing in more than one available row gets a method
  // subtitle so the rows are distinguishable; single-method providers stay
  // visually unchanged.
  const idCounts = new Map<string, number>();
  for (const { provider } of rows)
    idCounts.set(provider.id, (idCounts.get(provider.id) ?? 0) + 1);

  return (
    <div className="mt-8">
      <h2 className="text-sm font-semibold text-foreground mb-2">
        Available providers
      </h2>
      <div className="border border-border rounded-lg overflow-hidden bg-card divide-y divide-border">
        {rows.map(({ provider, authType }) => (
          <DisconnectedRow
            key={providerRowKey(provider.id, authType, provider.providerInstanceId)}
            provider={provider}
            authType={authType}
            scope={scope}
            showMethod={(idCounts.get(provider.id) ?? 0) > 1}
          />
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  provider: ProviderSummary;
  authType: CredentialAuthType;
  scope: Scope;
  showMethod: boolean;
}

function DisconnectedRow({ provider, authType, scope, showMethod }: RowProps) {
  const { error: showError } = useToast();
  const startOAuth = useStartOAuth();
  const [apiKeyModal, setApiKeyModal] = useState(false);
  const [oauthManualModal, setOauthManualModal] = useState(false);
  const [awsRoleModal, setAwsRoleModal] = useState(false);
  const [deviceStart, setDeviceStart] = useState<OAuthDeviceStart | null>(null);

  const startProviderOAuth = async () => {
    // OAuth start is user- or space-scope. Org hides the button (no
    // /oauth/{provider}/start route), so this branch is unreachable there.
    if (scope.kind === 'org') return;
    try {
      const result = await startOAuth.mutateAsync({
        scope: scope.kind === 'user' ? 'user' : { spaceId: scope.spaceId },
        provider: provider.id,
        body: {
          redirectUri: `${window.location.origin}/oauth/identity-callback`,
          returnUrl: `${window.location.origin}/oauth/connected`,
          // Instance-bound rows (interactive-OAuth MCP servers) carry the
          // server id; forward it so the backend resolves the per-server app
          // creds. Provider-wide rows leave it undefined — unchanged behavior.
          providerInstanceId: provider.providerInstanceId,
          // Generic MCP OAuth ('rfc') cannot build its provider without the
          // server URL — omitting it 500s with "serverUrl required to build an
          // RfcOauth2Provider". The catalog row already carries it, either
          // inline on an instance-bound row or flat on a provider-wide one;
          // named providers ignore it.
          ...((provider.mcpServer?.serverUrl ?? provider.mcpServerUrl)
            ? { serverUrl: provider.mcpServer?.serverUrl ?? provider.mcpServerUrl }
            : {}),
        },
      });
      // Device-authorization providers (aws-sso) hand back a verification URL +
      // user code instead of an authorizationUrl — route those to the modal.
      if (isDeviceStart(result)) {
        setDeviceStart(result);
      } else {
        openIdentityOAuthUrl(result.authorizationUrl, {
          provider: provider.id,
          scope: scope.kind,
        });
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not start OAuth');
    }
  };

  // The row's button is keyed purely off its single auth method.
  const renderConnect = () => {
    if (authType === 'oauth2') {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={startProviderOAuth}
          isLoading={startOAuth.isPending}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Connect
        </Button>
      );
    }
    if (authType === 'api_key') {
      return (
        <Button size="sm" variant="outline" onClick={() => setApiKeyModal(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add API token
        </Button>
      );
    }
    if (authType === 'aws_role') {
      return (
        <Button size="sm" variant="outline" onClick={() => setAwsRoleModal(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add IAM role
        </Button>
      );
    }
    return (
      <Button size="sm" variant="outline" onClick={() => setOauthManualModal(true)}>
        <Plus className="h-3.5 w-3.5 mr-1" />
        Add OAuth token
      </Button>
    );
  };

  return (
    <>
      <div className="flex items-center gap-4 px-4 py-2.5">
        <ProviderLogo
          slug={deriveLogoSlug(provider.id, provider.displayName)}
          displayName={provider.displayName}
          className="h-6 w-6"
          grayscale
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {provider.displayName}
          </p>
          {showMethod && (
            <p className="text-xs text-muted-foreground">
              {authMethodLabel(authType)}
            </p>
          )}
        </div>
        <div className="shrink-0">{renderConnect()}</div>
      </div>
      {apiKeyModal && (
        <AddApiKeyModal
          isOpen={apiKeyModal}
          onClose={() => setApiKeyModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {oauthManualModal && (
        <ConnectOAuthTokenModal
          isOpen={oauthManualModal}
          onClose={() => setOauthManualModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {awsRoleModal && (
        <AddAwsRoleModal
          isOpen={awsRoleModal}
          onClose={() => setAwsRoleModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {deviceStart && (
        <DeviceAuthModal
          isOpen={true}
          onClose={() => setDeviceStart(null)}
          provider={provider}
          device={deviceStart}
        />
      )}
    </>
  );
}
